import React, {useEffect, useState} from "react";
import {supabase} from "../../../../supabaseClient"; // Adjust path if needed
import "./ProductPage.css";

function ProductPage() {
    // Form fields for creating a new recipe
    const [receptNamn, setReceptNamn] = useState("");
    const [beskrivning, setBeskrivning] = useState("");
    const [kategori, setKategori] = useState("");
    const [malTyp, setMalTyp] = useState("");
    const [tillagningsinstruktioner, setTillagningsinstruktioner] = useState("");

    // Selected food items for the new recipe
    // We'll store objects: { id, name }
    const [selectedFoodItems, setSelectedFoodItems] = useState([]);

    // Search states: typed input & dynamic results
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);

    // --------------------------------------------
    // "Instant" search with a useEffect on searchTerm
    // --------------------------------------------
    useEffect(() => {
        const fetchFoodItems = async () => {
            if (!searchTerm.trim()) {
                // If no searchTerm, clear results
                setSearchResults([]);
                return;
            }
            try {
                // searchTerm is not empty => do a Supabase query
                const {data, error} = await supabase.from("fooditems").select("*").ilike("name", `%${searchTerm}%`); // case-insensitive partial match

                if (error) {
                    console.error("Error searching fooditems:", error);
                    return;
                }
                setSearchResults(data || []);
            } catch (err) {
                console.error("Unexpected error searching fooditems:", err);
            }
        };

        fetchFoodItems();
    }, [searchTerm]);

    // --------------------------------------------
    // User clicks on a search result to add it
    // --------------------------------------------
    const handleAddFoodItem = (item) => {
        // Avoid duplicates
        if (!selectedFoodItems.some((x) => x.id === item.id)) {
            setSelectedFoodItems((prev) => [...prev, item]);
        }
    };

    // --------------------------------------------
    // Remove a selected item by ID
    // --------------------------------------------
    const handleRemoveFoodItem = (foodId) => {
        setSelectedFoodItems((prev) => prev.filter((item) => item.id !== foodId));
    };

    // --------------------------------------------
    // Submit the new recipe + link the selected items
    // --------------------------------------------
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            // 1) Insert into "recipes"
            const {data, error} = await supabase
                .from("recipes")
                .insert([
                    {
                        name: receptNamn,
                        description: beskrivning,
                        category: kategori,
                        meal_type: malTyp,
                        instructions: tillagningsinstruktioner,
                    },
                ])
                .select();

            if (error) {
                console.error("Error inserting recipe:", error);
                alert("Ett fel uppstod vid skapandet av receptet.");
                return;
            }

            const insertedRow = data[0];
            const newRecipeId = insertedRow.id;

            // 2) Insert in "recipe_fooditems"
            if (selectedFoodItems.length > 0) {
                // Convert our item objects to rows with { recipe_id, food_id }
                const linkRows = selectedFoodItems.map((obj) => ({
                    recipe_id: newRecipeId,
                    food_id: obj.id,
                }));
                const {error: linkError} = await supabase.from("recipe_fooditems").insert(linkRows);

                if (linkError) {
                    console.error("Error linking fooditems:", linkError);
                    alert("Fel vid koppling av matvaror.");
                }
            }

            alert("Nytt recept tillagt i databasen!");

            // Clear form
            setReceptNamn("");
            setBeskrivning("");
            setKategori("");
            setMalTyp("");
            setTillagningsinstruktioner("");
            setSelectedFoodItems([]);
            setSearchTerm("");
            setSearchResults([]);
        } catch (err) {
            console.error("Unexpected error inserting recipe:", err);
            alert("Oväntat fel vid skapande av recept.");
        }
    };

    return (
        <div className="product-page-container">
            <h1>Skapa Nytt Recept</h1>
            <p>Fyll i formuläret för att skapa ett nytt recept.</p>

            <form className="recipe-form" onSubmit={handleFormSubmit}>
                {/* Basic recipe fields... */}
                <div className="form-group">
                    <label>Recept namn:</label>
                    <input type="text" value={receptNamn} onChange={(e) => setReceptNamn(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label>Beskrivning:</label>
                    <input type="text" value={beskrivning} onChange={(e) => setBeskrivning(e.target.value)} required />
                </div>

                <div className="form-group">
                    <label>Kategori:</label>
                    <select value={kategori} onChange={(e) => setKategori(e.target.value)} required>
                        <option value="">Välj kategori</option>
                        <option value="allätare">allätare</option>
                        <option value="veg">veg</option>
                        <option value="vegan">vegan</option>
                        <option value="allergi">allergi</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Mål typ:</label>
                    <select value={malTyp} onChange={(e) => setMalTyp(e.target.value)} required>
                        <option value="">Välj mål typ</option>
                        <option value="frukost">frukost</option>
                        <option value="middag">middag</option>
                        <option value="lunch">lunch</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Tillagningsinstruktioner:</label>
                    <textarea value={tillagningsinstruktioner} onChange={(e) => setTillagningsinstruktioner(e.target.value)} rows="3" required />
                </div>

                {/* 
          FOOD ITEM SEARCH: as user types, 
          we do a .ilike() query in the useEffect 
        */}
                <div className="form-group">
                    <label>Ingredienser:</label>
                    {/* Instant search input */}
                    <input type="text" placeholder="Sök matvara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="fooditem-search" />

                    {/* Show search results */}
                    {searchResults.length > 0 && (
                        <ul className="search-results-list">
                            {searchResults.map((item) => (
                                <li key={item.id} onClick={() => handleAddFoodItem(item)} className="search-result-item">
                                    {item.name}
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Show selected items as "chips" */}
                    {selectedFoodItems.length > 0 && (
                        <div className="selected-fooditems">
                            <p>Valda ingredienser:</p>
                            <div className="selected-items-container">
                                {selectedFoodItems.map((food) => (
                                    <div key={food.id} className="selected-item-chip">
                                        <span className="chip-text">{food.name}</span>
                                        <span className="chip-remove" onClick={() => handleRemoveFoodItem(food.id)}>
                                            x
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <button type="submit" className="submit-recipe-btn">
                    Skapa recept
                </button>
            </form>
        </div>
    );
}

export default ProductPage;
